import { Controller } from '@hotwired/stimulus';
import { post } from '@rails/request.js';

// Persists a reorder of the hiring steps (a drag or the move buttons). Wired
// from `accordion-component:reorder` on the list's Turbo Frame: posts the
// moved step's new 1-based position among the saved steps (the only items
// carrying a reorder URL) and renders the Turbo Stream answer, which paints
// the whole list again — the server order always wins.
export default class extends Controller {
  async reorder(event) {
    const { item } = event.detail;
    const steps = Array.from(this.element.querySelectorAll('[data-reorder-url]'));
    const refocus = this.focusMemo(item);

    try {
      const response = await post(item.dataset.reorderUrl, {
        body: { order: steps.indexOf(item) + 1 },
        responseKind: 'turbo-stream'
      });

      // A refused move answers 422 with a stream of its own (the saved order
      // and an error toast), which request.js has already rendered — reloading
      // here would wipe the toast before it is read. Anything else means
      // nothing repainted the list, so go and get the real one.
      if (!response.ok && !response.unprocessableEntity) {
        window.location.reload();
        return;
      }

      if (refocus) this.restoreFocus(refocus);
    } catch {
      // The move already happened in the DOM, so a request that never landed
      // would leave the employer looking at an order the server never saw.
      window.location.reload();
    }
  }

  // A step moved with Alt+Arrow has its toggle focused; remember it so the
  // shortcut can be pressed repeatedly to walk the step through the list.
  focusMemo(item) {
    const active = document.activeElement;
    if (!active || !item.contains(active)) return null;

    return active.matches('.ui-accordion-item__toggle') ? { itemId: item.id } : null;
  }

  // The stream replaces the whole list, dropping focus to the body; give it
  // back to the moved step's toggle. Only from the body: if the wait was long
  // enough for the user to focus something else — a modal that traps focus,
  // say — taking it back would strand them outside it.
  restoreFocus({ itemId }) {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const active = document.activeElement;
      if (active && active !== document.body) return;

      document.getElementById(itemId)?.querySelector('.ui-accordion-item__toggle')?.focus();
    }));
  }

}
